import { useEffect, useMemo, useRef } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { fetchEventTicketGroups, fetchSeatingLayout } from 'api/events';
import { useTableSeatingStore } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/store';
import { TransformedEventSeating } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';
import { formatApiData } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/transformers';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import { eventQueryKeys } from 'queries/event-query-keys';
import { TicketGroup } from 'types/domain';

const useInitializeSeating = () => {
    const hasInitialized = useRef(false);
    const { data: event } = useCurrentCampaignEvent();

    const { data: seatingLayout } = useSuspenseQuery({
        queryKey: eventQueryKeys.seatingLayout(event.id),
        queryFn: () => fetchSeatingLayout(event.id),
    });

    const { data: ticketGroups } = useSuspenseQuery({
        queryKey: eventQueryKeys.ticketGroups(event.id),
        queryFn: () => fetchEventTicketGroups(event.id),
    });

    const transformedData = useMemo((): TransformedEventSeating => {
        const response = {
            unseated_tickets: seatingLayout?.unseated_tickets || [],
            seating_groups: seatingLayout?.seating_groups || [],
        };
        const normalized = formatApiData(response);
        if (ticketGroups?.data) {
            normalized.ticketGroups = ticketGroups.data.reduce(
                (acc, group) => {
                    acc[group.id.toString()] = group;
                    return acc;
                },
                {} as Record<string, TicketGroup>,
            );
        }
        return normalized;
    }, [seatingLayout, ticketGroups]);

    const { initializeSeating, preserveUnsavedChanges, tickets, seatingGroups, isInitializing } =
        useTableSeatingStore(state => ({
            initializeSeating: state.initializeSeating,
            preserveUnsavedChanges: state.preserveUnsavedChanges,
            tickets: state.tickets,
            seatingGroups: state.seatingGroups,
            isInitializing: state.isInitializing,
        }));

    useEffect(() => {
        if (!hasInitialized.current) {
            initializeSeating(transformedData);
            hasInitialized.current = true;
        } else {
            preserveUnsavedChanges(transformedData);
        }
    }, [transformedData, initializeSeating, preserveUnsavedChanges]);

    return {
        tickets,
        seatingGroups,
        isInitializing,
    };
};

export { useInitializeSeating };
